import { Button } from "@spz/ui/components/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@spz/ui/components/select";
import { Typography } from "@spz/ui/components/typography";
import { cn } from "@spz/ui/lib/utils";
import type { ComponentPropsWithoutRef } from "react";
import { useTranslation } from "react-i18next";

const Pagination = ({
  className,
  ...props
}: ComponentPropsWithoutRef<"nav">) => (
  <nav
    aria-label="pagination"
    className={cn("justify-center", className)}
    {...props}
  />
);
Pagination.displayName = "Pagination";

interface PaginationContentProps extends ComponentPropsWithoutRef<"ul"> {
  ref?: React.Ref<HTMLUListElement>;
}
const PaginationContent: React.FC<PaginationContentProps> = ({
  className,
  ref,
  ...props
}) => (
  <ul
    ref={ref}
    className={cn("flex flex-row items-center gap-1", className)}
    {...props}
  />
);
PaginationContent.displayName = "PaginationContent";

interface PaginationItemProps extends ComponentPropsWithoutRef<"li"> {
  ref?: React.Ref<HTMLLIElement>;
}
const PaginationItem: React.FC<PaginationItemProps> = ({
  className,
  ref,
  ...props
}) => <li ref={ref} className={cn("", className)} {...props} />;
PaginationItem.displayName = "PaginationItem";

interface PaginationButtonProps extends ComponentPropsWithoutRef<"button"> {
  isActive?: boolean;
}

// Base styles for all pagination buttons
const paginationButtonStyles = cn(
  "hover:bg-transparent hover:text-primary", // Remove ghost button hover bg and add primary text color on hover
  "data-[state=open]:bg-transparent", // Remove background when button is in open state
  "active:bg-transparent", // Remove background on active state
  "select-none cursor-pointer",
);

interface PaginationButtonComponentProps extends PaginationButtonProps {
  ref?: React.Ref<HTMLButtonElement>;
}
const PaginationButton: React.FC<PaginationButtonComponentProps> = ({
  className,
  isActive,
  children,
  ref: _ref,
  ...props
}) => (
  <Button
    variant="ghost"
    className={cn(
      "!w-5.5 !h-5.5 p-0",
      paginationButtonStyles,
      isActive && "bg-gray-50 hover:bg-gray-50", // Keep gray background when active
      "transition-colors duration-0 ease-in-out",
      className,
    )}
    {...props}
  >
    {children}
  </Button>
);
PaginationButton.displayName = "PaginationButton";

interface PaginationNavigationProps extends ComponentPropsWithoutRef<"button"> {
  label: string;
}

interface PaginationPreviousProps extends PaginationNavigationProps {
  ref?: React.Ref<HTMLButtonElement>;
}
const PaginationPrevious: React.FC<PaginationPreviousProps> = ({
  className,
  label,
  ref: _ref,
  ...props
}) => (
  <Button
    variant="ghost"
    className={cn("p-0", "w-8 md:w-auto", paginationButtonStyles, className)}
    leftIcon="arrow-left"
    data-testid="pagination-previous"
    {...props}
  >
    <span className="hidden md:inline">{label}</span>
  </Button>
);
PaginationPrevious.displayName = "PaginationPrevious";

interface PaginationNextProps extends PaginationNavigationProps {
  ref?: React.Ref<HTMLButtonElement>;
}
const PaginationNext: React.FC<PaginationNextProps> = ({
  className,
  label,
  ref: _ref,
  ...props
}) => (
  <Button
    variant="ghost"
    className={cn("p-0", "w-8 md:w-auto", paginationButtonStyles, className)}
    rightIcon="arrow-right"
    data-testid="pagination-next"
    {...props}
  >
    <span className="hidden md:inline">{label}</span>
  </Button>
);
PaginationNext.displayName = "PaginationNext";

interface PaginationEllipsisProps extends ComponentPropsWithoutRef<"span"> {
  ref?: React.Ref<HTMLSpanElement>;
}
const PaginationEllipsis: React.FC<PaginationEllipsisProps> = ({
  className,
  ref,
  ...props
}) => (
  <span
    ref={ref}
    aria-hidden
    className={cn(
      "flex h-5.5 w-5.5 items-center justify-center text-gray-500",
      className,
    )}
    {...props}
  >
    ...
  </span>
);
PaginationEllipsis.displayName = "PaginationEllipsis";

export {
  Pagination,
  PaginationButton,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
};

export const DEFAULT_PAGE_SIZES = [50, 100];

export interface PageSelectorProps extends ComponentPropsWithoutRef<"div"> {
  pageSize: number;
  totalCount: number;
  currentPage: number; // Add current page
  onPageSizeChange: (size: number) => void;
  entityLabel: string;
  pageSizeOptions?: number[];
}

export interface PageSelectorComponentProps extends PageSelectorProps {
  ref?: React.Ref<HTMLDivElement>;
}

export const PageSelector: React.FC<PageSelectorComponentProps> = ({
  className,
  pageSize,
  totalCount,
  currentPage,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  ref,
  entityLabel,
  ...props
}) => {
  const { t } = useTranslation();

  const pageIndex = currentPage - 1;

  const rowsBefore = pageIndex * pageSize;
  const remainingRows = totalCount - rowsBefore;
  const rowsInPage = remainingRows >= pageSize ? pageSize : remainingRows;

  const startIndex = totalCount > 0 ? rowsBefore + 1 : 0;
  const endIndex = rowsBefore + rowsInPage;

  const displayPagination =
    pageSizeOptions.length > 0 && totalCount > pageSizeOptions[0];

  const entityLabelTranslation = t(entityLabel, {
    count: totalCount,
  });

  return (
    <div
      ref={ref}
      className={cn("inline-flex items-center gap-2", className)}
      {...props}
    >
      {displayPagination && (
        <div className="flex items-center flex-1">
          <span className="font-bold whitespace-nowrap mr-2">
            {t("pagination_show_per_page")}
          </span>

          <Select
            value={pageSize.toString()}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger
              className="w-9 border border-gray-300 rounded px-2 py-1"
              data-testid="pagination-items-per-page"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <Typography
        variant="body"
        className="text-gray-700 tabular-nums text-right"
      >
        {totalCount > 0
          ? t("pagination_current_page", {
              startIndex,
              endIndex,
              total: totalCount,
              entity: entityLabelTranslation,
            })
          : t("pagination_empty", {
              entity: entityLabelTranslation,
            })}
      </Typography>
    </div>
  );
};

PageSelector.displayName = "PageSelector";
